import { Button } from './Button';
import { plausibleEventClass } from '../lib/plausible';
import { useEventState } from '../lib/eventState';

const YOUTUBE_URL = 'https://www.youtube.com/@statefarm';
const TWITCH_URL = 'https://twitch.tv/StateFarm';

// Post-contest (GHS5-87): once the "Watch Season 5_Now" face is up, the watch
// CTAs point at the season archives instead of the live channels the Friday
// streams aired on.
const YOUTUBE_POST_CONTEST_URL = 'https://www.youtube.com/@statefarm/playlists';
const TWITCH_POST_CONTEST_URL = 'https://www.twitch.tv/statefarm/videos';

// PLAY NOW destination. Points at /play — the smart gate that routes the
// user based on auth + weekly consent state (anonymous → /register, PENDING →
// /register, ACTIVE + no consent → /weekly-rules, ACTIVE + consent → game).
// Single entry point for every PLAY NOW CTA on the site.
const PLAY_NOW_TO = '/play';

/**
 * Hero — home-page top section.
 *
 *   Layout (per the May-15 design snapshot):
 *     - Top row sits on the dark hero_full photo: GH logo + streaming text +
 *       YT/Twitch CTAs.
 *     - Below the top row is the "stage" — a flex-1 container split 45/55
 *       between an empty upper zone (hero_full visible, dimmed) and a
 *       rounded-rect gradient zone at the bottom.
 *     - The JNS lockup is absolutely positioned at the 45% mark of the stage
 *       (its vertical center lands exactly on the gradient's top edge) so the
 *       logo *straddles* both zones — top half on the photo, bottom half on
 *       the gradient.
 *     - The Secret Lab chair floats independently, anchored to the
 *       bottom-right of the gradient with a slight negative right offset so
 *       it visually overlaps the gradient's right edge.
 *     - PLAY NOW CTA + "FOR A CHANCE TO WIN" sit inside the gradient,
 *       centered horizontally, anchored to the gradient bottom via justify-end
 *       + pb-*.
 *
 *   Asset-driven sizing — lockup is 680px wide at xl (≥1280px) per design.
 *     /assets/images/jns/jns.svg  (single vector — scales cleanly at every breakpoint)
 *     /assets/images/secretlab/chair-{mobile,tablet,desktop}.png
 */
export default function HeroSection() {
  // Contest-end / Free Play (GHS5-85): drop the prize framing (chair + "for a
  // chance to win") and switch the streaming line to "Watch Season 5_Now".
  const { contestEnded } = useEventState();
  return (
    <section
      aria-labelledby="hero-heading"
      className="relative isolate flex min-h-[640px] flex-1 flex-col overflow-hidden sm:min-h-[720px]"
    >
      <h1 id="hero-heading" className="sr-only">
        State Farm Gamerhood Season 5 - Jake&apos;s Neighborhood Swarm
      </h1>

      {/*
        Hero background — autoplaying H.264 MP4 (yuv420p, the pixel format
        Safari/iOS require) with the static PNG as poster. The poster covers
        every case the video won't play: iOS Low Power Mode and Android Data
        Saver (which block muted autoplay), slow/cellular first paint, and
        the brief pre-decode frame. So a still hero is always shown rather
        than a blank box.
          - muted + playsInline + autoPlay + loop: the exact combo iOS needs
            to play inline; without playsInline iOS forces native fullscreen.
          - The two assets have very different aspect ratios (mp4 400x224
            landscape, png 585x1266 portrait), so the element is full-bleed
            (inset-0) with object-cover — video and poster are both scaled to
            fill the section, cropping overflow rather than letterboxing.
            object-center matches the old top-1/2 centering.
      */}
      <video
        autoPlay
        muted
        loop
        playsInline
        poster="/assets/images/backgrounds/hero_full.png"
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 -z-30 h-full w-full select-none object-cover object-center"
      >
        <source src="/assets/images/backgrounds/hero_full.mp4" type="video/mp4" />
      </video>

      {/*
        Background overlay stack — these are "filters" on hero_full per the
        Figma fill panel. Both layers cover the full section edge-to-edge at
        every breakpoint. Stacked in a single element via multi-value
        `background` shorthand:
          - Linear (top, 80% layer opacity, alphas pre-multiplied):
              direction top→bottom (0% at top, 100% at bottom)
              #1A0C2C 100% @ 27%  →  #2E1F3C 0% @ 95%
          - Radial (below linear, 60% layer opacity, alphas pre-multiplied):
              center at 50%/50%, explicit radii 50% horiz / 50% vert so the
              100% stop lands exactly on each edge midpoint (matches the
              Figma handle that drops from center straight down to the
              bottom edge). Default `farthest-corner` sizing was too loose —
              left the bottom at ~42% gradient progress, hence the
              "too bright" look.
              #2E1F3C 0% @ 26%  →  #0F0B15 100% @ 100%
      */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 -z-20"
        style={{
          background: [
            'linear-gradient(to bottom, rgba(26, 12, 44, 0.8) 27%, rgba(46, 31, 60, 0) 95%)',
            'radial-gradient(ellipse 50% 50% at 50% 50%, rgba(46, 31, 60, 0) 26%, rgba(15, 11, 21, 0.6) 100%)',
          ].join(', '),
        }}
      />

      <div className="relative mx-auto flex w-full max-w-[1280px] flex-1 flex-col gap-4 px-5 pb-8 pt-6 sm:gap-6 sm:px-12 sm:pb-12 sm:pt-8">
        <div className="flex flex-col items-center justify-between gap-5 pb-2 pt-2 md:flex-row md:items-start md:gap-6 md:pb-0 md:pt-1">
          <img
            src="/assets/gh-logo.svg"
            alt="State Farm Gamerhood"
            width={300}
            height={75}
            className="h-auto w-[clamp(260px,62vw,680px)] shrink-0 md:h-[75px] md:w-auto"
          />

          <div className="flex w-full flex-col items-center gap-4 md:items-end xl:w-auto xl:flex-row xl:items-center xl:gap-5">
            <p className="font-geist text-center text-[clamp(14px,4.0vw,22px)] leading-tight text-white md:text-right">
              {contestEnded ? (
                <span className="font-bold">Watch Season 5_Now</span>
              ) : (
                <>
                  <span className="font-normal">
                    Streaming Fridays starting
                    <br />
                    July 31_
                  </span>
                  <span className="font-bold">6PM EDT</span>
                </>
              )}
            </p>

            <div className="flex items-center gap-3 sm:gap-[15px]">
              <a
                href={contestEnded ? YOUTUBE_POST_CONTEST_URL : YOUTUBE_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Watch on YouTube"
                className={`relative inline-flex h-[30px] w-[100px] items-center justify-center rounded-full bg-gradient-to-r from-[#900f3a] to-[#fe0137] shadow-[0_0_4px_#ff0048,-5px_5px_5px_rgba(0,0,0,0.25),inset_0_4px_4px_rgba(231,111,151,0.4)] transition hover:scale-[1.04] focus:outline-none focus:ring-2 focus:ring-white ${plausibleEventClass('Watch Click', { platform: 'YouTube' })}`}
              >
                <YouTubeIcon />
              </a>
              <a
                href={contestEnded ? TWITCH_POST_CONTEST_URL : TWITCH_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Watch on Twitch"
                className={`relative inline-flex h-[30px] w-[100px] items-center justify-center rounded-full bg-gradient-to-r from-[#560fad] to-[#9f50ff] shadow-[0_0_4px_#5044ff,-5px_5px_5px_rgba(0,0,0,0.25),inset_0_4px_4px_rgba(165,111,231,0.4)] transition hover:scale-[1.04] focus:outline-none focus:ring-2 focus:ring-white ${plausibleEventClass('Watch Click', { platform: 'Twitch' })}`}
              >
                <TwitchIcon />
              </a>
            </div>
          </div>
        </div>

        {/*
          Stage — gradient pinned to the bottom of remaining space via
          `justify-end`. Lockup + CTA group + chair all live INSIDE the
          gradient container in normal flex flow (lockup → gap → CTA group).
          This gives explicit control over the lockup→Button gap, and the
          gradient's container can never push the lockup up into the top
          row's streaming text.
        */}
        <div className="relative flex flex-1 flex-col justify-end">
          {/*
            Gradient fill — stops tuned for the new in-flow layout. The
            lockup now occupies the upper portion of the container, so the
            transparent zone is pushed lower so the lockup reads as floating
            on the dark hero_full photo rather than on red:
              0%  #FF0015 100% opacity   (opaque red at bottom — PLAY NOW)
              30% #FF0051 60%            (transition begins above CTA)
              55% #FF0051  0%            (fully transparent — covers lockup)
              55-100%                    (held transparent)
            Adjust stop percentages to taste — the visible red zone height
            is purely a function of where the stops sit, independent of the
            container's actual rendered height.
          */}
          <div
            className="relative flex flex-col items-center gap-6 rounded-[15px] px-4 pb-8 pt-6 sm:gap-8 sm:rounded-[15px] sm:px-8 sm:pb-12 sm:pt-8"
            style={{
              background:
                'linear-gradient(to top, #ff0015 0%, rgba(255, 0, 81, 0.6) 30%, rgba(255, 0, 81, 0) 55%)',
            }}
          >
            {/*
              JNS lockup — first flex child, in normal flow.
              Sizing strategy (width truth lives on the <picture>):
                - below md: clamp(260px, 62vw, 680px) — viewport-driven scale
                - md+ (tablet 800px–1279px and desktop 1280+): fills the
                  gradient's inner area via w-full, capped at 680. The
                  gradient's px-4 / sm:px-8 padding gives the lockup
                  breathing room from the container edges.
              The <img> uses `w-full` so it always matches the picture's
              size. Aspect-ratio is preserved via the width/height attrs.
              Re-exported PNG ships without baked drop shadow — applied
              here via CSS so it's tunable.
            */}
            <picture className="block w-[clamp(260px,72vw,680px)] md:w-[90%] md:max-w-[680px]">
              {/* <source
                media="(max-width: 640px)"
                srcSet="/assets/images/jns/jns-mobile.png"
              /> */}
              {/* <source
                media="(max-width: 1024px)"
                srcSet="/assets/images/jns/jns-tablet.png"
              /> */}
              <img
                src="/assets/images/jns/jns.svg"
                alt="Jake's Neighborhood Swarm"
                width={880}
                height={607}
                className="pointer-events-none w-full select-none drop-shadow-[0_0_36px_rgba(255,45,58,0.5)]"
              />
            </picture>

            {/*
              Mobile-only prize section — slots between the JNS lockup and
              the CTA group at <md. The chair is inline here (left text,
              right image); the absolute-positioned chair below is hidden
              at this breakpoint and re-appears at md+. Removed entirely once
              the contest ends (Free Play has no prize).
            */}
            {!contestEnded && (
              <div
                className="flex w-full items-center justify-between rounded-[15px] px-4 py-3 md:hidden"
                style={{
                  background:
                    'linear-gradient(to bottom, rgba(61, 47, 69, 0) 0%, rgba(61, 47, 69, 0.4) 100%)',
                }}
              >
                <p className=" font-[Orbitron] text-[clamp(14px,4.0vw,28px)] leading-snug text-white">
                  Play for a chance<br/> to win a State<br/> Farm themed <br/>Secretlab TITAN<br/> Evo chair!
                </p>
                <img
                  src="/assets/images/secretlab/chair-double.webp"
                  alt=""
                  aria-hidden="true"
                  width={110}
                  height={111}
                  className="w-[40%] min-w-[120px] select-none drop-shadow-[0_18px_24px_rgba(0,0,0,0.45)]"
                />
              </div>
            )}

            {/* CTA group — Button + caption, tight inner gap */}
            <div className="flex w-full flex-col items-center gap-3 sm:gap-4">
              <Button
                variant="primary"
                to={PLAY_NOW_TO}
                className={`w-[60%] max-w-[300px] md:w-[300px] ${plausibleEventClass('Play Click', { location: 'hero' })}`}
              >
                Play Now
              </Button>
              {!contestEnded && (
                <p className="hidden font-heading uppercase text-white sm:block sm:text-[13px] sm:tracking-[0.32em]">
                  For a chance to win
                </p>
              )}
            </div>

            {/*
              Chair — md+ only (mobile shows the inline chair inside the
              prize section above). Absolute, bottom-right of the gradient.
              `hidden md:block` on the <picture> hides it AND prevents the
              browser from loading the desktop chair art when it'll never
              render at this breakpoint. Removed once the contest ends.
            */}
            {!contestEnded && (
              <picture className="hidden md:block">
                <img
                  src="/assets/images/secretlab/chair-double.webp"
                  alt=""
                  aria-hidden="true"
                  width={480}
                  height={484}
                  className="pointer-events-none absolute bottom-4 right-4 w-[26%] select-none drop-shadow-[0_18px_24px_rgba(0,0,0,0.45)] xl:w-[28%]"
                />
              </picture>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function YouTubeIcon() {
  return (
    <svg
      viewBox="0 0 25 18"
      aria-hidden="true"
      className="h-[18px] w-[25px]"
      fill="currentColor"
    >
      <path
        fill="#fff"
        d="M24.477 2.83a3.13 3.13 0 0 0-2.21-2.214C20.32 0 12.5 0 12.5 0S4.68 0 2.733.616A3.13 3.13 0 0 0 .523 2.83C0 4.79 0 9 0 9s0 4.21.523 6.17a3.13 3.13 0 0 0 2.21 2.214C4.68 18 12.5 18 12.5 18s7.82 0 9.767-.616a3.13 3.13 0 0 0 2.21-2.214C25 13.21 25 9 25 9s0-4.21-.523-6.17ZM9.94 12.75v-7.5L16.45 9l-6.51 3.75Z"
      />
    </svg>
  );
}

function TwitchIcon() {
  return (
    <svg
      viewBox="0 0 17 20"
      aria-hidden="true"
      className="h-[20px] w-[17px]"
      fill="#fff"
    >
      <path d="M1.523 0 0 3.426V14.43h4.063V17h2.286l2.158-2.57h3.302L17 9.435V0H1.523Zm13.572 8.586-2.539 2.527h-3.302L7.111 13.43v-2.317H3.81V1.713h11.286v6.873ZM13.477 4.286h-1.524v4.586h1.524V4.286Zm-4.063 0H7.889v4.586h1.525V4.286Z" />
    </svg>
  );
}
